import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Calendar, MapPin, ArrowLeft, FileText, Download, 
  Mail, Music, ExternalLink, Check, X, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

export default function CompetitionPagePreview() {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  
  useEffect(() => {
    axios.get(`${API}/competition-pages/${pageId}/preview`, { withCredentials: true })
      .then(res => {
        setPageData(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        if (err.response?.status === 403) {
          setError('You do not have permission to preview this page.');
        } else {
          setError('Competition page not found.');
        }
        setLoading(false);
      });
  }, [pageId]);
  
  const formatDateRange = (startDate, endDate) => {
    if (!startDate) return 'TBA';
    try {
      const start = new Date(startDate);
      if (endDate) {
        const end = new Date(endDate);
        const sameDay = start.getDate() === end.getDate() && start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
        if (sameDay) {
          return start.toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' });
        }
        if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
          return `${start.getDate()}-${end.getDate()} ${start.toLocaleDateString('en-NZ', { month: 'long', year: 'numeric' })}`;
        }
        return `${start.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })}`;
      }
      return start.toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch {
      return startDate;
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBA';
    try {
      return new Date(dateStr).toLocaleDateString('en-NZ', { 
        day: 'numeric', month: 'long', year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  const handleApprove = async () => {
    if (!pageData?.pending_approval?.approval_id) {
      toast.error('No pending approval request found');
      return;
    }
    
    setApproving(true);
    try {
      await axios.post(`${API}/approvals/${pageData.pending_approval.approval_id}/comment`, {
        comment: 'Approved via preview page',
        action: 'approved'
      }, { withCredentials: true });
      toast.success('Page approved!');
      navigate('/admin/approvals');
    } catch (error) {
      toast.error('Failed to approve page');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!pageData?.pending_approval?.approval_id) {
      toast.error('No pending approval request found');
      return;
    }
    
    const reason = prompt('Please provide a reason for rejection:');
    if (!reason) {
      toast.error('Rejection reason is required');
      return;
    }
    
    setRejecting(true);
    try {
      await axios.post(`${API}/approvals/${pageData.pending_approval.approval_id}/comment`, {
        comment: reason,
        action: 'rejected'
      }, { withCredentials: true });
      toast.success('Page rejected');
      navigate('/admin/approvals');
    } catch (error) {
      toast.error('Failed to reject page');
    } finally {
      setRejecting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#2D4A3E] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-stone-600">Loading preview...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <Card className="p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Preview Unavailable</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Link to="/admin/approvals">
            <Button variant="outline">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Approvals
            </Button>
          </Link>
        </Card>
      </div>
    );
  }
  
  const { page, competition, host_organisation, venue, published_documents, pending_approval } = pageData;
  
  return (
    <div className="min-h-screen bg-stone-100">
      {/* Preview Banner */}
      <div className="bg-amber-500 text-white py-3 px-4 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5" />
            <span className="font-medium">
              Preview Mode - Page Status: 
              <Badge className={`ml-2 ${
                page.status === 'approved' ? 'bg-green-600' :
                page.status === 'pending' ? 'bg-yellow-600' :
                page.status === 'rejected' ? 'bg-red-600' :
                'bg-gray-600'
              }`}>
                {page.status}
              </Badge>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/approvals">
              <Button variant="ghost" size="sm" className="text-white hover:bg-amber-600">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Approvals
              </Button>
            </Link>
            {pending_approval && (
              <>
                <Button 
                  size="sm" 
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={handleApprove}
                  disabled={approving || rejecting}
                  data-testid="preview-approve-btn"
                >
                  <Check className="mr-2 h-4 w-4" />
                  {approving ? 'Approving...' : 'Approve'}
                </Button>
                <Button 
                  size="sm" 
                  variant="destructive"
                  onClick={handleReject}
                  disabled={approving || rejecting}
                  data-testid="preview-reject-btn"
                >
                  <X className="mr-2 h-4 w-4" />
                  {rejecting ? 'Rejecting...' : 'Reject'}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Hero Header */}
      <div className="bg-gradient-to-r from-[#2D4A3E] to-[#3D5A4E] text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          {/* Disciplines badges */}
          <div className="flex justify-center gap-2 mb-4">
            {competition.artistic_events_included && (
              <Badge className="bg-white/20 text-white border-white/30">Artistic</Badge>
            )}
            {competition.figure_events_included && (
              <Badge className="bg-white/20 text-white border-white/30">Figures</Badge>
            )}
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {page.title || competition.name}
          </h1>
          
          {competition.year && (
            <p className="text-xl text-white/80">{competition.year}</p>
          )}
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-4xl mx-auto py-12 px-4 space-y-8">
        {/* Intro Paragraph */}
        {page.content && (
          <Card className="p-6">
            <div 
              className="prose prose-stone max-w-none"
              dangerouslySetInnerHTML={{ __html: page.content }}
            />
          </Card>
        )}
        
        {/* When and Where */}
        <Card className="p-6">
          <h2 className="text-2xl font-bold text-[#2D4A3E] mb-4 flex items-center gap-2">
            <Calendar className="h-6 w-6" />
            When and Where?
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Venue */}
            <div>
              <h3 className="font-semibold text-lg mb-2">Venue</h3>
              {venue ? (
                <div className="space-y-1 text-stone-600">
                  <p className="font-medium text-stone-800">{venue.venue_name}</p>
                  {venue.street && <p>{venue.street}</p>}
                  {venue.suburb && <p>{venue.suburb}</p>}
                  <p>{venue.city} {venue.postcode}</p>
                  {venue.google_maps_url && (
                    <a 
                      href={venue.google_maps_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[#2D4A3E] hover:underline mt-2"
                    >
                      <MapPin className="h-4 w-4" />
                      View on Google Maps
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              ) : (
                <p className="text-stone-500">Venue to be announced</p>
              )}
            </div>
            
            {/* Dates */}
            <div>
              <h3 className="font-semibold text-lg mb-2">Competition Dates</h3>
              <p className="text-stone-600 mb-4">
                {formatDateRange(competition.start_date, competition.end_date)}
              </p>
              
              <h3 className="font-semibold text-lg mb-2">Registration Deadline</h3>
              <p className="text-stone-600">
                {formatDate(competition.registration_deadline)}
              </p>
            </div>
          </div>
        </Card>
        
        {/* Music Submission */}
        <Card className="p-6 bg-amber-50 border-amber-200">
          <h2 className="text-2xl font-bold text-[#2D4A3E] mb-4 flex items-center gap-2">
            <Music className="h-6 w-6" />
            Music Submission
          </h2>
          <p className="text-stone-600">
            Music submission details will be available closer to the competition date. 
            Please check back later or contact the host organisation for more information.
          </p>
        </Card>
        
        {/* Competition Documents */}
        {published_documents && published_documents.length > 0 && (
          <Card className="p-6">
            <h2 className="text-2xl font-bold text-[#2D4A3E] mb-4 flex items-center gap-2">
              <FileText className="h-6 w-6" />
              Competition Documents
            </h2>
            
            <div className="space-y-3">
              {published_documents.map(doc => (
                <div 
                  key={doc.document_id}
                  className="flex items-center justify-between p-3 bg-stone-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-[#2D4A3E]" />
                    <span className="font-medium">{doc.document_name}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    (Download disabled in preview)
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}
        
        {/* Contact */}
        {host_organisation && (
          <Card className="p-6">
            <h2 className="text-2xl font-bold text-[#2D4A3E] mb-4 flex items-center gap-2">
              <Mail className="h-6 w-6" />
              Contact
            </h2>
            
            <div className="space-y-2">
              <p className="font-medium">{host_organisation.name}</p>
              {host_organisation.contact_email && (
                <a 
                  href={`mailto:${host_organisation.contact_email}`}
                  className="text-[#2D4A3E] hover:underline"
                >
                  {host_organisation.contact_email}
                </a>
              )}
            </div>
          </Card>
        )}

        {/* Questions Form (disabled in preview) */}
        <Card className="p-6 opacity-60">
          <h2 className="text-2xl font-bold text-[#2D4A3E] mb-4">
            Questions or Comments
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            (Form disabled in preview mode)
          </p>
        </Card>
      </div>
    </div>
  );
}
